<template>
  <layout-content-vertical-nav :nav-menu-items="menuItems">
    <slot></slot>

    <selecionar-pausa-dialog :is-pausa-dialog-open.sync="openPausaDialog" />

    <!-- Slot: Navbar -->
    <template #navbar="{ toggleVerticalNavMenuActive }">
      <div
        class="navbar-content-container"
        :class="{'expanded-search': shallShowFullSearch}"
      >
        <!-- Left Content: Search -->
        <div class="d-flex align-center">
          <v-icon
            class="me-3"
            @click="toggleVerticalNavMenuActive"
          >
            {{ icons.mdiMenu }}
          </v-icon>
        </div>

        <div class="d-flex align-center right-row">
          <app-bar-user-menu></app-bar-user-menu>
        </div>
      </div>
    </template>

    <!-- Slot: Footer -->
    <template #footer>
      <div class="d-flex justify-space-between ">
        <div class="align-center d-none d-md-flex">
          <v-icon
            color="error"
            class="ms-2"
          >
            {{ icons.mdiHeartOutline }}
          </v-icon>
        </div>
      </div>
    </template>
  </layout-content-vertical-nav>
</template>

<script>

import AppBarUserMenu from '@core/layouts/components/app-bar/AppBarUserMenu.vue'
import { getVuetify } from '@core/utils'
import { mdiHeartOutline, mdiMenu } from '@mdi/js'
import { onMounted, ref, watch } from '@vue/composition-api'
import useAppConfig from '@core/@app-config/useAppConfig'
import { storeToRefs } from 'pinia'
import LayoutContentVerticalNav from '@/@core/layouts/variants/content/vertical-nav/LayoutContentVerticalNav.vue'
import appBarSearchData from '@/assets/app-bar-search-data'

import SelecionarPausaDialog from '@/views/pausas/SelecionarPausaDialog.vue'
import MenuService from '@/services/modules/MenuService'
import usePausaStore from '@/store/pausas'

export default {
  components: {
    LayoutContentVerticalNav,
    AppBarUserMenu,
    SelecionarPausaDialog,
  },

  setup() {
    const $vuetify = getVuetify()

    // Search
    const appBarSearchQuery = ref('')
    const shallShowFullSearch = ref(false)
    const maxItemsInGroup = 5
    const totalItemsInGroup = ref({
      pages: 0,
      files: 0,
      contacts: 0,
    })

    const menuItems = ref([])

    async function getMenuItems() {
      menuItems.value = await MenuService.get()
    }

    watch(appBarSearchQuery, () => {
      totalItemsInGroup.value = {
        pages: 0,
        files: 0,
        contacts: 0,
      }
    })

    const { id: pausaId, bloquearTela: pausaBloquearTela } = storeToRefs(usePausaStore())
    const continuarPausa = ref(false)
    const openPausaDialog = ref(false)

    /*
     * Watch em vez de checagem única no onMounted: precisa reagir quando usePausaValidation
     * (App.vue) zera o store porque a pausa persistida no localStorage não existe mais no
     * backend. Uma checagem só no onMounted já teria decidido (errado) antes dessa validação,
     * que é assíncrona, terminar.
     */
    watch(
      () => [pausaId.value, pausaBloquearTela.value],
      ([id, bloquearTela]) => {
        const deveAbrir = !!id && bloquearTela

        continuarPausa.value = deveAbrir
        openPausaDialog.value = deveAbrir
      },
      { immediate: true },
    )

    onMounted(getMenuItems)

    const { appBarType } = useAppConfig()

    watch(
      () => $vuetify?.breakpoint.mdAndDown,
      value => {
        appBarType.value = value ? 'fixed' : 'hidden'
      },
      {
        immediate: true,
      },
    )

    const searchFilterFunc = (item, queryText, itemText) => {
      if (item.header || item.divider) return true

      const itemGroup = (() => {
        if (item.to !== undefined) return 'pages'
        if (item.size !== undefined) return 'files'
        if (item.email !== undefined) return 'contacts'

        return null
      })()

      const isMatched = itemText.toLocaleLowerCase().indexOf(queryText.toLocaleLowerCase()) > -1

      if (isMatched) {
        if (itemGroup === 'pages') totalItemsInGroup.value.pages += 1
        else if (itemGroup === 'files') totalItemsInGroup.value.files += 1
        else if (itemGroup === 'contacts') totalItemsInGroup.value.contacts += 1
      }

      return appBarSearchQuery.value && isMatched && totalItemsInGroup.value[itemGroup] <= maxItemsInGroup
    }

    // ? Handles case where in <lg vertical nav menu is open and search is triggered using hotkey then searchbox is hidden behind vertical nav menu overlaty
    const handleShallShowFullSearchUpdate = (isVerticalNavMenuActive, toggleVerticalNavMenuActive) => {
      if ($vuetify.breakpoint.mdAndDown && isVerticalNavMenuActive) {
        toggleVerticalNavMenuActive()
      }
    }

    return {
      handleShallShowFullSearchUpdate,
      menuItems,

      // Search
      appBarSearchQuery,
      shallShowFullSearch,
      appBarSearchData,
      searchFilterFunc,

      continuarPausa,
      openPausaDialog,

      icons: {
        mdiMenu,
        mdiHeartOutline,
      },
    }
  },
}
</script>

<style lang="scss" scoped>
.navbar-content-container {
  height: 10%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-grow: 1;
  position: relative;
}

// ? Handle bg of autocomplete for blured appBar
.v-app-bar.bg-blur {
  .expanded-search {
    ::v-deep .app-bar-autocomplete-box div[role='combobox'] {
      background-color: transparent;
    }

    > .d-flex > button.v-icon {
      display: none;
    }

    // ===

    & > .right-row {
      visibility: hidden;
      opacity: 0;
    }

    ::v-deep .app-bar-search-toggler {
      visibility: hidden;
    }
  }
}
</style>
